import React from "react";
import { ExclamationCircleOutlined } from "@ant-design/icons";
import { Typography } from "antd";
import { differenceInYears, isBefore, subYears } from "date-fns";
import { phoneNumberLengthRegEx } from "../../../../utils/formValidation";

const zipInputRules = [
  {
    pattern: /^\d{5}$/,
    message: ""
  }
];

const onlyWhitespacesRule = {
  whitespace: true,
  message: ""
};

const emailInputRules = [
  onlyWhitespacesRule,
  {
    type: "email",
    message: ""
  }
];

const npiInputRule = {
  pattern: /^\d{10}$/,
  message: ""
};

// Genentech free-drug address guard (rejects an address that leads with an apartment/unit token
// instead of a street name). Single source of truth: the JSON form renderer references it by string
// (rules: ["addressNotStartingWithAptUnitRule"]) and the pre-JSON free-drug section field bank
// (genentechPapFieldsConfiguration.js) uses the rule object directly.
const addressNotStartingWithAptUnitRule = {
  validator: (_, value) => {
    if (!value) return Promise.resolve();

    const startsWithAptUnit = /^(APT|UNIT|#|STE|SUITE)/.test(value.trim().toUpperCase());
    return startsWithAptUnit ? Promise.reject(new Error("Address must start with a street name")) : Promise.resolve();
  }
};

const phoneNumberLengthRule = {
  pattern: phoneNumberLengthRegEx,
  message: ""
};

const isUnderEighteen = (dob) => {
  const dobDate = new Date(dob);
  const today = new Date();

  const age = differenceInYears(today, dobDate);
  return age < 18 || (age === 18 && isBefore(today, subYears(dobDate, 18)));
};

const noFutureDateRule = ({ message } = {}) => ({
  validator: (rule, value) => {
    if (value && isBefore(new Date(), new Date(value))) {
      return Promise.reject(
        <Typography.Text type="danger">
          <ExclamationCircleOutlined style={{ marginRight: 5 }} />
          {message}
        </Typography.Text>
      );
    }
    return Promise.resolve();
  }
});

const patientMinAgeRule = ({ minAge, message }) => ({
  validator: (rule, value) => {
    if (!value) return Promise.resolve();
    const age = differenceInYears(new Date(), new Date(value));
    if (age < minAge) {
      return Promise.reject(
        <Typography.Text type="danger">
          <ExclamationCircleOutlined style={{ marginRight: 5 }} />
          {message}
        </Typography.Text>
      );
    }
    return Promise.resolve();
  }
});

const patientCantBeUnderEighteenRule = ({ message }) => ({
  validator: (rule, value) => {
    if (isUnderEighteen(value)) {
      return Promise.reject(
        <Typography.Text type="danger">
          <ExclamationCircleOutlined style={{ marginRight: 5 }} />
          {message}
        </Typography.Text>
      );
    }
    return Promise.resolve();
  }
});

// Genentech's address line rejects whitespace other than a normal space/tab/CR/LF
// (non-breaking space, form-feed, vertical-tab, Unicode spaces, BOM) plus zero-width
// characters -- invisible chars that ride along on copy-paste. The copay form
// normalizes them on prefill so an auto-filled address is clean; the authoritative
// server-side normalization lives in FN (FhirBridgeService.sanitizeAddressLines).
// Per team review we sanitize rather than block the user. See MVP-31013.
const ADDRESS_ZERO_WIDTH_CHARS = "\u200B-\u200D\uFEFF"; // ZWSP, ZWNJ, ZWJ, BOM
const addressZeroWidthCharsGlobal = new RegExp(`[${ADDRESS_ZERO_WIDTH_CHARS}]`, "g");

// Strip zero-width/BOM, collapse whitespace runs to a single space, trim. Exported
// for the copay prefill (initialValues.js); mirrors the FN server-side sanitizer.
const sanitizeAddressLine = (value) =>
  typeof value === "string" ? value.replace(addressZeroWidthCharsGlobal, "").replace(/\s+/g, " ").trim() : value;

const mustMatchAllowedValues = ({ allowedValues, message }) => ({
  validator: (rule, value) => {
    if (!allowedValues.includes(value)) {
      return Promise.reject(
        <Typography.Text type="danger">
          <ExclamationCircleOutlined style={{ marginRight: 5 }} />
          {message}
        </Typography.Text>
      );
    }
    return Promise.resolve();
  }
});

const regexRule = ({ regex, message }) => ({
  pattern: regex,
  message: message
});

const lessOrEqualRule = (t, max, fieldName) => ({
  max: max,
  message: t("errorMessages.should_be_less_than_or_equal", {
    fieldName: fieldName,
    maxCharacter: max
  })
});

// JSON-form max-length rule: a single-params factory so renderReactFromJson can resolve it
// from a field's `rules`/`rulesParams` (the engine calls factory rules with one params object).
const maxLengthRule = ({ max, message }) => ({
  max,
  message
});

const PAYER_NAME_MAX_LENGTH = 50;

// no special characters rule preventing all special characters except hyphens and underscores
const noSpecialCharactersRule = {
  pattern: /^[a-zA-Z0-9\s\-_]*$/,
  message: "Special characters are not allowed"
};
const minNumberValidation =
  ({ minNumber }) =>
  () => ({
    validator(_, value) {
      const numValue = Number(value);
      if (isNaN(numValue)) {
        return Promise.reject();
      }
      if (numValue < minNumber) {
        return Promise.reject();
      }
      return Promise.resolve();
    }
  });

export {
  zipInputRules,
  npiInputRule,
  addressNotStartingWithAptUnitRule,
  emailInputRules,
  onlyWhitespacesRule,
  phoneNumberLengthRule,
  noFutureDateRule,
  patientMinAgeRule,
  patientCantBeUnderEighteenRule,
  mustMatchAllowedValues,
  sanitizeAddressLine,
  regexRule,
  lessOrEqualRule,
  maxLengthRule,
  PAYER_NAME_MAX_LENGTH,
  noSpecialCharactersRule,
  minNumberValidation
};
